import { Input } from 'src/components/widgets/input';
import { round } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import { ValidationStateProvider, useValidationContext } from 'src/shared/validation';
import styled from 'styled-components';
import { Unit } from './helpers';
import { Button } from '../buttons';
import { FormControl } from '../formControl';
import Stack from '../stack/stack';

const Container = styled.div`
    position: absolute;
    left: 100px;
    top: 100px;
    padding: 20px;
    background-color: white;
    border-radius: 5px;
    box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.2);
    max-width: 250px;
`;

interface RulerInputProps {
    size: number;
    unit: Unit;
    x: number;
    y: number;
    onConfirm: (size: number) => void;
}

const RulerInput: FC<RulerInputProps> = ({ size, unit, x, y, onConfirm }) => {
    const [value, setValue] = useState(size > 0 ? round(size, 3).toString() : '');
    const ref = useRef<HTMLInputElement>(null);

    const handleConfirm = () => {
        onConfirm(parseFloat(value));
    };

    useEffect(() => {
        if (ref.current) {
            ref.current.focus();
            ref.current.select();
        }
    }, []);

    return (
        <ValidationStateProvider>
            <Container style={{ left: x, top: y }}>
                <Stack>
                    <FormControl label={`${T._('Size', 'Admin')} (${unit.value})`}>
                        <Input
                            type='number'
                            min='0'
                            value={value}
                            onChange={(e) => setValue(e.currentTarget.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                            ref={ref}
                            validationKey='rule-measure'
                            message={T._('Please enter a valid measure', 'Admin')}
                            validate={(value) => {
                                if (value === '') {
                                    return false;
                                }
                                const parsedValue = parseInt(value);
                                return isNaN(parsedValue) || parsedValue <= 0;
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <ConfirmButton handleConfirm={handleConfirm} value={value} />
                    </FormControl>
                </Stack>
            </Container>
        </ValidationStateProvider>
    );
};

function ConfirmButton({ handleConfirm, value }: { handleConfirm: () => void; value: string }) {
    const { isValid } = useValidationContext();

    // We check if the value is empty because I don't want to show an error message at start.
    return (
        <Button onClick={handleConfirm} disabled={value == '' || !isValid()}>
            {T._('Ok', 'Admin')}
        </Button>
    );
}

export default RulerInput;
